import {
  IConfiguredDirectTicket,
  IOwnerData,
  IPersonalData,
  ITicketOptions,
} from '../tariff/direct-ticket-validation.interfaces';
import { IPurchase, PaymentMethod } from '../payment-methods.interfaces';
import { ERelationType } from '../cms/text-relation-type.enums';
import {
  IAdditionalEntryValue,
  TariffProductDefinition,
  TariffProductGroup,
} from '../tariff/product-group.interfaces';
import {
  EConfigurationChannel,
  EPaymentProviders,
} from '../cms/configuration/configuration.enums';
import { ITicket } from '../cms/ticket/ticket.interfaces';
import { IBooking, IParsedBooking } from './booking.interfaces';
import { Brand } from '../branded-types';
import { IOnDemandOrderData } from '../on-demand/on-demand-shared.interfaces';

export const DIRECT_TICKET_CART_KEY = 'DIRECT_TICKET_CART_KEY';
export const DIRECT_TICKET_ADD_PAYMENT = 'DIRECT_TICKET_ADD_PAYMENT';
export const AUTHORIZE_PAYMENT = 'AUTHORIZE_PAYMENT';
export const USER_GUEST_ID = 'USER_GUEST_ID';

export enum EOrderStatus {
  complete = 0,
  inProgress = 1,
  cancelled = 2,
  rejected = 3,
  pendingPaymentVerification = 4,
}

export type OrderId = Brand<string, 'orderId'>;

export interface IOrderedTicket {
  id: OrderId;
  passenger: Partial<IOwnerData>;
  personalData: Partial<IPersonalData>;
  name: string;
  number: number;
  createdAt: Date;
  options: Partial<ITicketOptions>;
  marketing: ITicket | undefined;
  productGroup: TariffProductGroup;
  productDefinition: TariffProductDefinition;
  additionalEntries: IAdditionalEntryValue[];
}

export interface IExtendedParsedOrder extends IParsedOrder {
  paymentMethodName: string;
  totalPrice: {
    priceInCents: number;
    formattedPrice: string;
  };
}

//@TODO for cancellation OrderEvents message: affected TicketId and then add EventTypes for ticket specific actions

export interface IParsedOrder {
  id: OrderId;
  source: 'webShop' | 'app' | 'import';
  paymentProvider: EPaymentProviders;
  userId: string;
  personalData: Partial<IPersonalData>;
  paymentMethod: Partial<PaymentMethod>;
  purchase: Partial<IPurchase['purchase']>;
  bookings: IParsedBooking[];
  events: IOrderEvent[];
  createdAt: string;
  comment: string;
  onDemand?: IOnDemandOrderData & {
    cancellationReason?: string;
  };
}

export interface IParsedOrders {
  activeOrders: IParsedOrder[];
  archivedOrders: IParsedOrder[];
}

export interface IOrders {
  activeOrders: IOrder[];
  archivedOrders: IOrder[];
}

export enum ETransactionSource {
  webShop = 'webShop',
  app = 'app',
  import = 'import',
}

export interface IOrder {
  id?: OrderId;
  email?: string;
  source: ETransactionSource;
  paymentProvider: EPaymentProviders;
  transactionId: string;
  userId: string;
  providerResponseDump: string;
  onDemandDataDump?: string;
  events: IOrderEvent[];
  createdAt: string;
  paymentMethodId: string;
  personalDataDump: string;
  paymentMethodDump: string;
  orderPriceWithVat: number;
  vat: number;
  comment: string;
  bookings: IBooking[];
}

export interface ITransactionRequest {
  tickets: IConfiguredDirectTicket[];
  paymentMethod?: PaymentMethod;
  source: ETransactionSource;
  paymentProvider: EPaymentProviders;
  shouldSaveFellowTravelers: boolean;
}

export interface IGuestTransactionRequest {
  tickets: IConfiguredDirectTicket[];
  customerId: string;
  source: ETransactionSource;
  paymentProvider: EPaymentProviders;
}

export interface IOrderNotificationEvent {
  userId: string;
  clientChannel: EConfigurationChannel;
}

export interface IOrderConfirmEvent extends IOrderNotificationEvent {
  orderId: OrderId;
}

export interface IOrderCancellationEvent extends IOrderNotificationEvent {
  orderId: OrderId;
  reason: string;
}

export interface IOrderEvent {
  id?: string;
  eventType: EOrderEventType;
  message: string;
  createdAt: string;
}

export enum EOrderEventType {
  failed = 'OrderFailed',
  created = 'OrderCreated',
  validated = 'OrderValidated',
  paymentReserved = 'OrderPaymentReserved',
  paid = 'OrderPaid',
  entitlementsCreated = 'OrderEntitlementsCreated',
  securityFeaturesCreated = 'OrderSecurityFeaturesCreated',
  documentsCreated = 'DocumentsCreated',
  emailsSend = 'EmailsSend',
  salesDataCreated = 'SalesDataCreated',
  refunded = 'OrderRefunded',
}

export enum EOrderOrigin {
  DirectTicketConfig = 'configuration',
  AccountPage = 'account',
  OnDemandTicketConfig = 'on-demand',
}

export const DEFAULT_ORDERS = {
  activeOrders: [],
  archivedOrders: [],
};

export enum OrderHistorySubMenuAction {
  resend = 0,
  download = 1,
}

export interface IOrderHistoryItem {
  text: string;
  action: OrderHistorySubMenuAction;
}

export const DEFAULT_ORDER: IOrder = {
  comment: '',
  orderPriceWithVat: 499,
  paymentMethodId: '00000000-0000-0000-0000-000000000000',
  transactionId: '00000000-0000-0000-0000-000000000000',
  vat: 199,
  createdAt: '01.01.2025',
  providerResponseDump: 'test data',
  events: [],
  paymentProvider: EPaymentProviders.Logpay,
  personalDataDump: '',
  paymentMethodDump: '',
  source: ETransactionSource.webShop,
  userId: '00000000-0000-0000-0000-000000000000',
  bookings: [],
};

export const paymentTypeToRelationType = (
  type: string
): ERelationType | undefined => {
  switch (type) {
    case 'SEPA_DIRECT_DEBIT':
      return ERelationType.sepaDebit;
    case 'CREDIT_CARD':
      return ERelationType.creditCard;
    case 'PAYDIREKT':
      return ERelationType.payDirect;
    case 'GOOGLE_PAY':
      return ERelationType.googlePay;
    case 'PAYPAL':
      return ERelationType.paypal;
    case 'APPLE_PAY':
      return ERelationType.applePay;
    case 'PREPAY':
      return ERelationType.prePay;
    case 'AMAZON_PAY':
      return ERelationType.amazonPay;
    case 'BONVOYO':
      return ERelationType.bonVoyo;
    default:
      return undefined;
  }
};

export const convertConfiguredToOrderedTickets = (
  tickets: IConfiguredDirectTicket[]
): IOrderedTicket[] => {
  return tickets
    .map(
      (ticket: IConfiguredDirectTicket): Omit<IOrderedTicket, 'number'>[] => {
        return (
          ticket.owners?.map((passenger): Omit<IOrderedTicket, 'number'> => {
            return {
              id: crypto.randomUUID() as OrderId,
              passenger,
              personalData: ticket.personalData,
              name: ticket.name,
              createdAt: new Date(),
              options: ticket.product,
              productGroup: ticket.tariffProductGroup,
              marketing: ticket.ticket,
              additionalEntries: ticket.additionalEntries ?? [],
              productDefinition: {
                keyElements: ticket.keyElements,
                priceLevel: ticket.priceLevel,
                riderType: ticket.riderType,
                discountClass: ticket.discountClass,
                comfortClass: ticket.comfortClass,
                ticketInvalidations: ticket.ticketInvalidations,
                numberOfPeople: ticket.numberOfPeople,
                numberOfDrives: ticket.numberOfDrives,
                via: ticket.via,
                transferable: ticket.transferable,
                validityOptions: ticket.validityOptions,
                minNumberOfPeople: ticket.minNumberOfPeople,
                maxNumberOfPeople: ticket.maxNumberOfPeople,
                standardNumberOfPeople: ticket.standardNumberOfPeople,
                canBeCancelled: ticket.canBeCancelled,
                additionalValues: ticket.additionalValues,
                layout: ticket.layout,
                numberOfSections: ticket.numberOfSections,
                serviceArea: ticket.serviceArea,
                versionNumber: ticket.versionNumber,
                sort: ticket.sort,
                externalReference: ticket.externalReference,
                price: ticket.price,
              },
            };
          }) ?? []
        );
      }
    )
    .reduce((acc, curr) => acc.concat(curr), [])
    .map((ticket, index): IOrderedTicket => {
      return {
        ...ticket,
        number: index + 1,
      };
    });
};
